/**
 * Decorator that guards async methods - returns early if provider is not connected.
 * Use on methods that should only execute when LMS connection is active.
 *
 * @example
 * @requireConnection
 * async setSuspendData(data: ScormSuspendData): Promise<void> {
 *   SCORM_API_Wrapper.setSuspendData(data)
 * }
 */
export function requireConnection<Args extends unknown[], Return>(
  _target: object,
  _propertyKey: string,
  descriptor: TypedPropertyDescriptor<(...args: Args) => Promise<Return | undefined>>,
): TypedPropertyDescriptor<(...args: Args) => Promise<Return | undefined>> {
  const originalMethod = descriptor.value

  if (!originalMethod) {
    return descriptor
  }

  descriptor.value = async function (
    this: { connected: boolean },
    ...args: Args
  ): Promise<Return | undefined> {
    if (!this.connected) {
      return undefined
    }
    return originalMethod.apply(this, args)
  }

  return descriptor
}

/**
 * Decorator variant that returns null instead of undefined when not connected.
 * Useful for getter methods that return nullable values.
 */
export function requireConnectionOrNull<Args extends unknown[], Return>(
  _target: object,
  _propertyKey: string,
  descriptor: TypedPropertyDescriptor<(...args: Args) => Promise<Return | null>>,
): TypedPropertyDescriptor<(...args: Args) => Promise<Return | null>> {
  const originalMethod = descriptor.value

  if (!originalMethod) {
    return descriptor
  }

  descriptor.value = async function (
    this: { connected: boolean },
    ...args: Args
  ): Promise<Return | null> {
    if (!this.connected) {
      return null
    }
    return originalMethod.apply(this, args)
  }

  return descriptor
}
